import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyPaginator as MatPaginator, MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatLegacyTable as MatTable, MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { State } from '../../../dave-data-module/State';
import { LengthPipe } from '../../../helper/length.pipe';
import { LedgerImportSelectTableItem, WarehouseMovementLedgerImportSelectTableDatasource } from './warehouse-movement-ledger-import-select-table-datasource';
import { CustomLabelService } from '../../../services/custom-label/custom-label.service';

@Component({
    selector: 'app-warehouse-movement-ledger-import-select-table',
    templateUrl: './warehouse-movement-ledger-import-select-table.component.html',
    styleUrls: ['./warehouse-movement-ledger-import-select-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, MatTableModule, MatSortModule, MatPaginatorModule, LengthPipe, MatCheckboxModule],
})
export class WarehouseMovementLedgerImportSelectTableComponent implements AfterViewInit, OnDestroy {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatTable) table!: MatTable<LedgerImportSelectTableItem>;
    dataSource: WarehouseMovementLedgerImportSelectTableDatasource;

    /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
    displayedColumns: Array<keyof LedgerImportSelectTableItem | 'checkbox'> = ['checkbox', 'CommissionNumber', 'CommissionInternalNumber', 'CommissionDescription', 'Customer', 'Address', 'ConsolidatedInvoiceId', 'Status', 'Type', 'CreatedAt'];
    autoDisplayedColumns: Array<keyof LedgerImportSelectTableItem> = ['ConsolidatedInvoiceId', 'CommissionInternalNumber', 'CommissionDescription', 'Address', 'Status', 'Type', 'CreatedAt'];
    columnHeaders$: Observable<{ [key in keyof LedgerImportSelectTableItem]: string }> = this.cls.getSingle$('CommissionInternalNumber').pipe(
        map(CommissionInternalNumber => ({
            ConsolidatedInvoiceId: 'Nr.',
            CommissionId: '',
            isSelected$: '',
            Id: '',
            Type: 'Typ',
            Customer: '',
            CommissionNumber: '',
            CommissionInternalNumber,
            CommissionDescription: 'Bezeichnung',
            Address: 'Adresse',
            Status: 'Status',
            CreatedAt: 'Erstellt am',
            CreatedAtAsMillis: '',
        }))
    );
    protected emptyText = 'keine Einträge vorhanden';
    protected customerLabel$: Observable<string> = this.cls.getSingle$('CustomerSupplier');
    protected commissionLabel$: Observable<string> = this.cls.getLabels$('Commission').pipe(
        map((label) => {
            return label.wordBinding
        })
    );
    protected multiple$ = new BehaviorSubject<boolean>(false);
    protected disableUncheckedCheckboxes$: Observable<boolean>;
    private selectableLedgerImportIds$ = new BehaviorSubject<number[]>([]);
    @Input() set selectableLedgerImportIds(ids: number[]) {
        if (JSON.stringify(ids) !== JSON.stringify(this.selectableLedgerImportIds$.value)) {
            this.selectableLedgerImportIds$.next([...ids]);
        }
    }
    @Output() selectedLedgerImports = new EventEmitter<number[]>();

    @Input()
    set multiple(value: boolean) {
        this.multiple$.next(coerceBooleanProperty(value));
    }
    get multiple() {
        return this.multiple$.value;
    }
    // tslint:disable-next-line: member-ordering naming-convention
    static ngAcceptInputType_multiple: BooleanInput;
    @Input()
    set hideCheckbox(value: boolean) {
        if (coerceBooleanProperty(value)) {
            this.displayedColumns = this.displayedColumns.filter((c) => c !== 'checkbox');
        }
        // todo checkbox hinzufügen wenn false
    }
    // tslint:disable-next-line: member-ordering naming-convention
    static ngAcceptInputType_hideCheckbox: BooleanInput;
    changeSubscription: Subscription | undefined;
    constructor(
        store: Store<State>,
        @Inject(LOCALE_ID) locale: string,
        protected cls: CustomLabelService
    ) {
        this.dataSource = new WarehouseMovementLedgerImportSelectTableDatasource(store, locale, this.selectableLedgerImportIds$);
        this.changeSubscription = this.dataSource.selectedLedgerImports$.subscribe((val) => this.selectedLedgerImports.emit([...val]));
        this.disableUncheckedCheckboxes$ = combineLatest([this.multiple$, this.dataSource.selectedLedgerImports$]).pipe(
            map(([multi, selected]) => !multi && selected.length > 0),
            distinctUntilChanged(),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.table.dataSource = this.dataSource;
    }
    toggleSelection(liId: number) {
        const isSelected = this.dataSource.selectedLedgerImports$.value.includes(liId);
        if (isSelected) {
            this.dataSource.selectedLedgerImports$.next(this.dataSource.selectedLedgerImports$.value.filter((id) => id !== liId));
        } else if (this.multiple$.value) {
            this.dataSource.selectedLedgerImports$.next([...this.dataSource.selectedLedgerImports$.value, liId]);
        } else {
            this.dataSource.selectedLedgerImports$.next([liId]);
        }
    }
    selectAll(select: boolean) {
        if (select) {
            firstValueFrom(this.dataSource.data$).then((data) => {
                this.dataSource.selectedLedgerImports$.next(data.map((d) => d.Id));
            });
        } else {
            this.dataSource.selectedLedgerImports$.next([]);
        }
    }
    ngOnDestroy(): void {
        this.changeSubscription?.unsubscribe();
    }
}
